import React, { useEffect, useState } from "react";
import {
  BackButton,
  Box,
  ButtonStyled,
  LabelStyled,
  SpanStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ContentWrapper } from "../../../components/styled/styled";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import { useForm } from "react-hook-form";
import { ManageDepartmentWrapper } from "./styled";
import HQBranchesApi from "../../../services/api/hqBranchesApi";
import { LazyLoadDropdown } from "../../../components/common/dropdowns/LazyLoadDropdown";
import { useUser } from "../../../context/userContext";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import PhoneInputField from "../../../components/common/inputFields/PhoneInput";
import { useDispatch } from "react-redux";
import { setBranchesDeps } from "../../../store/slices/helperSlice";

const EditDepartment = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const dispatch = useDispatch();
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const searchParam = new URLSearchParams(location.search);
  const hqBranchId = searchParam.get("org-structure");
  const departmentId = searchParam.get("department");

  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const handleSearch = (searchQuery) => {
    getCitiesList(currentPage, searchQuery);
  };

  const handleLoadMore = () => {
    getCitiesList(currentPage + 1);
  };

  const getCitiesList = async (pageNum = 1, searchQuery) => {
    if (citiesLoading) return;
    try {
      setCitiesLoading(true);
      const response = await HQBranchesApi.getCitiesFromCountry(
        user.country_id,
        pageNum,
        searchQuery
      );
      if (response.code === 200) {
        const citiesData = response?.data?.data.map((city) => ({
          label: language === "en" ? city.en_name : city.ar_name,
          value: city.id,
        }));
        if (searchQuery) {
          setFilteredCities(citiesData);
        } else {
          setCities((prevCities) => [...prevCities, ...citiesData]);
        }
        setHasMore(!!response.data.next_page_url);
        setCurrentPage(pageNum);
      }
    } catch (error) {
    } finally {
      setCitiesLoading(false);
    }
  };

  const getBranchesDepartments = async () => {
    try {
      const { data, code } = await HQBranchesApi.getBranchesDepartments();

      if (code === 200) {
        const branchesDepsArr = data?.map((item) => ({
          id: item?.id,
          name: item?.name,
          subLabel: item?.location?.name,
          location_id: item?.location_id,
        }));

        dispatch(setBranchesDeps(branchesDepsArr));
      }
    } catch (error) {}
  };

  const getDepartment = async () => {
    try {
      const { data, code } = await HQBranchesApi.getSingleDepartment(
        hqBranchId,
        departmentId
      );
      if (code === 200) {
        reset({
          id: data.id,
          name_en: data.name_en,
          name_ar: data.name_ar,
          initial_name: data.initial_name,
          city_id: data.city?.id,
          building: data.building,
          floor: data.floor,
          office: data.office,
          address: data.address,
          contact_no: data.contact_no,
          email: data.email,
        });
        const selectedCity = {
          label: data?.city?.en_name,
          value: data?.city?.id,
        };
        if (
          cities &&
          !cities.some((city) => city?.id === selectedCity?.value)
        ) {
          setCities((prevCities) => [...prevCities, selectedCity]);
        }
      }
    } catch (error) {
    } finally {
      setDataLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    const newFormData = {
      ...formData,
      _method: "PUT",
    };
    try {
      setLoading(true);
      const response = await HQBranchesApi.updateDepartment(
        hqBranchId,
        departmentId,
        newFormData
      );
      if (response.code === 200) {
        toast.success(t("department_updated"));
        getBranchesDepartments();
        navigate(`/org-structure/departments?org-structure=${hqBranchId}`);
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setDataLoading(true);
        const response = await HQBranchesApi.getCitiesFromCountry(
          user.country_id,
          currentPage
        );
        if (response.code === 200) {
          const citiesData = response?.data?.data.map((city) => ({
            label: language === "en" ? city.en_name : city.ar_name,
            value: city.id,
          }));
          setCities(citiesData);
          setHasMore(!!response.data.next_page_url);
        }
      } catch (error) {
      } finally {
        getDepartment();
      }
    };

    fetchInitialData();
  }, [hqBranchId, departmentId, user, language]);

  return (
    <ManageDepartmentWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("edit_department")}
            onClick={() =>
              navigate(`/org-structure/departments?org-structure=${hqBranchId}`)
            }
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <ContentWrapper className="content-box">
        {dataLoading ? (
          <Box className="p-5 m-5">
            <ThemeLoader />
          </Box>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Box className="pt-4">
              <Box>
                <ValidatedInput
                  name={"name_en"}
                  label={t("name_en")}
                  placeholder={t("english_name")}
                  className={"border-0"}
                  staric={true}
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
                <ValidatedInput
                  name={"name_ar"}
                  label={t("name_ar")}
                  placeholder={t("arabic_name")}
                  className={"border-0"}
                  staric={true}
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
                <ValidatedInput
                  name={"initial_name"}
                  label={t("initial_name")}
                  placeholder={t("initial_name")}
                  className={"border-0"}
                  // staric={true}
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Box>
            </Box>
            <Box className="mt-4">
              <SpanStyled
                size={"20px"}
                weight="800"
                color="var(--text)"
                className="px-4"
              >
                {t("location")}
              </SpanStyled>
              <Box className="d-flex align-items-center px-4 hqbranch-form-item py-4 formEleContainer">
                <Box className="form-label">
                  <LabelStyled size="15px" color="var(--text)">
                    {t("City")}
                  </LabelStyled>
                </Box>
                <LazyLoadDropdown
                  name="city_id"
                  control={control}
                  label={t("name_en")}
                  placeholder={t("select_city")}
                  options={cities}
                  filteredOptions={filteredCities}
                  loading={citiesLoading}
                  hasMore={hasMore}
                  onSearch={handleSearch}
                  onLoadMore={handleLoadMore}
                  value={watch("city")}
                  validationRules={{ required: false }}
                />
              </Box>
              <ValidatedInput
                name={"building"}
                label={t("building")}
                placeholder={t("building")}
                className={"border-0"}
                // staric={true}
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
              <ValidatedInput
                name={"floor"}
                label={t("floor")}
                placeholder={t("floor")}
                className={"border-0"}
                // staric={true}
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
              <ValidatedInput
                name={"office"}
                label={t("office")}
                placeholder={t("office")}
                className={"border-0"}
                // staric={true}
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
              <ValidatedInput
                name={"address"}
                label={t("address")}
                placeholder={t("address")}
                className={"border-0"}
                // staric={true}
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
            </Box>
            <Box className="mt-4">
              <SpanStyled
                size={"20px"}
                weight="800"
                color="var(--text)"
                className="px-4"
              >
                {t("contact")}
              </SpanStyled>
              <PhoneInputField
                name="contact_no"
                label={"contact_no"}
                placeholder={"contact_no"}
                className={"border-0"}
                // staric={true}
                register={register}
                control={control}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
              <ValidatedInput
                name={"email"}
                label={t("contact_email")}
                type={"input"}
                fieldType={"email"}
                placeholder={t("emailPlaceholder")}
                className={"border-0"}
                // staric={true}
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
            </Box>
            <Box className="d-flex justify-content-end gap-3 p-4">
              <ButtonStyled
                label={t("Cancel")}
                bg={"var(--bgSecondary)"}
                color={"var(--text)"}
                type="button"
                onClick={() =>
                  navigate(`/org-structure/departments?org-structure=${hqBranchId}`)
                }
              />
              <ButtonStyled
                label={t("Submit")}
                bg={"var(--themeColor)"}
                type="submit"
                isLoading={loading}
              />
            </Box>
          </Form>
        )}
      </ContentWrapper>
    </ManageDepartmentWrapper>
  );
};

export default EditDepartment;
